// src/stores/useRepeatPrescriptionRequestStore.ts
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';

import { PatientDetails } from '../types';
import { useVerifyPatientDetails } from './useVerifyPatientDetails';
import {
  fetchRepeatPrescriptionRequestApi,
  submitRepeatPrescriptionRequestApi,
} from '../services/repeat-prescription-requests.service';
import {
  sendVerificationCodeApi,
  verifyPatientDetailsAndCodeApi,
} from '../services/session.service';
import { PrescriptionMedication } from '../types/repeat-prescription-request';
import { useVerifyNhsLogin } from './useVerifyNhsLogin';
import { useApiLoading } from '../lib/composables/useApiLoading';
import { useSubmitAction } from '../lib/composables/useSubmitAction';
import { usePatientIdentity } from '../lib/composables/usePatientIdentity';

export const useRepeatPrescriptionRequestStore = defineStore('repeat-prescription-request', () => {
  // these blocks can be extracted into a function
  const expiryTime = ref<Date | null>(null); // +1 hour

  const { apiLoading, setApiLoading, setApiDone } = useApiLoading();

  //
  const sessionToken = ref<string | null>(null);
  const transactionId = ref<string | null>(null);
  const isEmergency = ref<boolean>(false);
  const {
    patientDetails,
    isValidPatient,
    // isVerified — 2FA passed; isNhsVerified — NHS login OK
    isVerified,
    isNhsVerified,
    setPatientDetails,
    setIsValidPatient,
    setIsVerified: setIsVerifiedValue,
    setIsNhsVerified,
    resetIdentity,
  } = usePatientIdentity();
  //

  const prescriptionsList = ref<PrescriptionMedication[]>([]);
  const prescriptionsAcuteList = ref<PrescriptionMedication[]>([]);

  const selectedPrescriptionsList = ref<string[]>([]);
  const selectedPrescriptionsAcuteList = ref<string[]>([]);

  // Optional free-text note the patient can attach to the whole request.
  const additionalInformation = ref<string>('');

  const isExpired = computed(
    () => expiryTime.value !== null && Date.now() > expiryTime.value.getTime(),
  );

  const isProxy = ref(false);

  function startNew() {
    createTransactionId();
    expiryTime.value = new Date(Date.now() + 60 * 60 * 1000);
    isEmergency.value = false;
    resetIdentity();
    prescriptionsList.value = [];
    prescriptionsAcuteList.value = [];
    selectedPrescriptionsList.value = [];
    selectedPrescriptionsAcuteList.value = [];
    additionalInformation.value = '';
  }

  function endCurrent() {
    startNew();
    expiryTime.value = null;
    transactionId.value = null;
  }

  // Callers disagree on arity — the service passes `true`, verifyNhsLogin passes
  // nothing — and it has only ever meant "2FA passed".
  const setIsVerified = () => setIsVerifiedValue(true);

  function createTransactionId() {
    transactionId.value = crypto.randomUUID();
  }

  // Preserves the existing state when the same patient is re-submitting their patient details
  function ensureJourneyForPatient(details: PatientDetails) {
    const isSamePatient =
      transactionId.value !== null &&
      !isExpired.value &&
      !isVerified.value &&
      patientDetails.value !== null &&
      patientDetails.value.firstName === details.firstName &&
      patientDetails.value.lastName === details.lastName &&
      patientDetails.value.postcode === details.postcode &&
      patientDetails.value.dateOfBirth === details.dateOfBirth;
    if (!isSamePatient) {
      startNew();
    }
  }

  function setIsEmergency(value: boolean) {
    isEmergency.value = value;
  }

  function setPrescriptionsList(value) {
    prescriptionsList.value = value;
  }
  function setPrescriptionsAcuteList(value) {
    prescriptionsAcuteList.value = value;
  }

  function setSelectedPrescriptionsList(value) {
    selectedPrescriptionsList.value = value;
  }

  function setSelectedPrescriptionsAcuteList(value) {
    selectedPrescriptionsAcuteList.value = value;
  }

  function setAdditionalInformation(value: string) {
    additionalInformation.value = value ?? '';
  }

  async function send2FAVerificationCode() {
    if (apiLoading.value) return null;
    setApiLoading();
    try {
      return await sendVerificationCodeApi(patientDetails.value, transactionId.value, true);
    } finally {
      setApiDone();
    }
  }

  const verifyPatientDetailsRaw = useVerifyPatientDetails({
    patientDetails,
    isProxy: ref(false),
    transactionId,
    setApiLoading,
    setApiDone,
    setIsValidPatient,
    setIsVerified,
    sendVerificationCodeOnSuccess: true,
  });

  const verifyPatientDetails = async (details: PatientDetails) => {
    if (apiLoading.value) return null;
    ensureJourneyForPatient(details);
    return await verifyPatientDetailsRaw(details);
  };

  const verifyPatientAndCode = async (verificationCode) => {
    if (apiLoading.value) return null;
    setApiLoading();
    try {
      return await verifyPatientDetailsAndCodeApi({
        patientDetails: patientDetails.value,
        transactionId: transactionId.value,
        verificationCode,
        setIsVerified,
      });
    } finally {
      setApiDone();
    }
  };

  const verifyNhsLogin = async (code: string, csrfToken: string) => {
    const run = useVerifyNhsLogin({
      code,
      csrfToken,
      setApiLoading,
      setApiDone,
      setIsValidPatient,
      setIsNhsVerified,
      setPatientDetails,
    });

    const result = await run();
    if (result.ok) {
      setIsVerified();
    }
    return result;
  };

  async function fetchPrescriptions() {
    setApiLoading();
    try {
      const result = await fetchRepeatPrescriptionRequestApi();

      if (!result.ok) {
        return { ok: false };
      }

      setPrescriptionsList(result.data.repeatPrescriptions);
      setPrescriptionsAcuteList(result.data.acutePrescriptions);
      return { ok: true };
    } catch (e) {
      console.error('fetchRepeatPrescriptionRequestApi error', e);

      return { ok: false };
    } finally {
      setApiDone();
    }
  }

  // inspectResult: false — this submit has never branched on the response body;
  // only a thrown error fails it.
  const submitAction = () => {
    const note = additionalInformation.value?.trim() ?? '';

    return useSubmitAction({ setApiLoading, setApiDone, endCurrent, inspectResult: false })(() =>
      submitRepeatPrescriptionRequestApi({
        prescriptionIds: [
          ...(selectedPrescriptionsList.value ?? []),
          ...(selectedPrescriptionsAcuteList.value ?? []),
        ],
        // Omit the note entirely when the patient left it blank, so a request
        // with no note is never treated as non-routine on the backend.
        ...(note ? { additionalInformation: note } : {}),
      }));
  };

  return {
    additionalInformation,
    apiLoading,
    expiryTime,
    isNhsVerified,
    isProxy,
    isValidPatient,
    isVerified,
    patientDetails,
    prescriptionsList,
    prescriptionsAcuteList,
    selectedPrescriptionsList,
    selectedPrescriptionsAcuteList,
    sessionToken,
    transactionId,

    // getters
    isExpired,

    // actions
    endCurrent,
    ensureJourneyForPatient,
    fetchPrescriptions,
    send2FAVerificationCode,
    setIsEmergency,
    setIsVerified,
    setPatientDetails,
    setSelectedPrescriptionsList,
    setSelectedPrescriptionsAcuteList,
    setAdditionalInformation,
    startNew,
    submitAction,
    verifyPatientDetails,
    verifyPatientAndCode,
    verifyNhsLogin,
  };
});
